
import { useState } from 'react'
import { METRIC_LABELS, thumbnailUrl } from '../lib/api'
import { InfoDot } from './InfoDot'
import { abbreviate, formatNumber, signed } from '../lib/numbers'

/**
 * The pieces the Stories and Spotlights tables share: a thumbnail cell, a KPI
 * row, and an inline sparkline.
 */

export const numberStyle = { fontVariantNumeric: 'tabular-nums' } as const

/** Absent renders as an em dash. **Never as 0** — zero is a measurement. */
/**
 * One table cell. **Full number with separators** — a table is read to check a
 * figure, and `380.8M` cannot be checked (HR-3).
 *
 * `null` is an em dash, never `0`: zero is a measurement and absent is not.
 * A negative is red parentheses, which this app really does produce.
 */
export function metricCell(value: number | null | undefined) {
  if (value === null || value === undefined) {
    return <span style={{ color: 'var(--text-label)' }}>—</span>
  }
  const { text, negative } = signed(value)
  return negative ? <span style={{ color: 'var(--status-critical)' }}>{text}</span> : text
}

/**
 * Portrait 26×46, because Snap content is 9:16 (`DESIGN-SYSTEM.md`).
 *
 * The image comes from our own bucket through a signed URL. **Snap's CDN URL is
 * never used**: 29 of 30 sampled were alive after 22 days, and once one 404s the
 * image is gone for good. Until the mirror reaches an asset the cell shows a
 * placeholder rather than a broken image — and on stories that matters more than
 * it sounds, because Snap gives a story no title at all, so this is the only
 * thing identifying the episode until ClickUp lands.
 */
export function Thumb({ assetId, has, alt }: { assetId: string; has: boolean; alt: string }) {
  // A mirror that fails to load shows the empty frame, not the browser's
  // broken-image icon with its alt text spilling out (seen 7 Oct).
  const [failed, setFailed] = useState(false)
  const frame = {
    width: 'var(--thumb-w, 26px)',
    // `min-width` matters: a `width` on a table cell is only a suggestion, and
    // once a table has enough columns to overflow its container the browser
    // squeezes the narrowest one. Measured on /spotlights, which has two more
    // columns than /stories: the image rendered **2px wide** while the same
    // component on /stories was correct at 26.
    minWidth: 'var(--thumb-w, 26px)',
    height: 'var(--thumb-h, 46px)',
    borderRadius: 'var(--radius-sm)',
    background: 'var(--bg-page)',
    border: '1px solid var(--border)',
    objectFit: 'cover' as const,
    display: 'block',
  }
  if (!has || failed) {
    return (
      <span
        style={{ ...frame, display: 'inline-block' }}
        title={failed ? 'Image Not Available' : 'Not Saved Yet'}
        aria-label="Thumbnail Not Available"
      />
    )
  }
  return (
    <button
      type="button"
      className="thumb-button"
      onClick={() => openLightbox(thumbnailUrl(assetId), alt)}
      aria-label={`Enlarge: ${alt}`}
      title="Click to Enlarge"
    >
      <img
        src={thumbnailUrl(assetId)}
        alt={alt}
        loading="lazy"
        style={frame}
        onError={() => setFailed(true)}
      />
    </button>
  )
}

/**
 * **ST-20 · SP-7 — click a thumbnail to see it at full size.**
 *
 * One native `<dialog>` for the whole app rather than one per row: a table of
 * 50 rows would otherwise mount 50 dialogs. `showModal()` gives the modal
 * behaviour for free — Esc closes, focus stays inside, the page behind is inert
 * — and the browser returns focus to the thumbnail that opened it.
 *
 * Always the **mirrored** image from our own bucket, never Snap's CDN, whose
 * URLs rotate (CLAUDE.md). A mirror that fails to load says so rather than
 * showing a broken-image icon.
 */
let lightbox: HTMLDialogElement | null = null

function ensureLightbox(): HTMLDialogElement {
  if (lightbox && document.body.contains(lightbox)) return lightbox
  const dialog = document.createElement('dialog')
  dialog.className = 'lightbox'
  dialog.setAttribute('aria-label', 'Enlarged Thumbnail')
  dialog.innerHTML =
    '<button type="button" class="lightbox-close" aria-label="Close">×</button>' +
    '<img class="lightbox-image" alt="" />' +
    '<p class="lightbox-missing" hidden>Image Not Available</p>'
  const image = dialog.querySelector('img')!
  const missing = dialog.querySelector('p')!
  image.addEventListener('error', () => {
    image.hidden = true
    missing.hidden = false
  })
  dialog.querySelector('button')!.addEventListener('click', () => dialog.close())
  // A click on the backdrop lands on the dialog element itself; a click on the
  // image or the button does not.
  dialog.addEventListener('click', (event) => {
    if (event.target === dialog) dialog.close()
  })
  document.body.appendChild(dialog)
  lightbox = dialog
  return dialog
}

export function openLightbox(src: string, alt: string) {
  const dialog = ensureLightbox()
  const image = dialog.querySelector('img')!
  const missing = dialog.querySelector('p')!
  image.hidden = false
  missing.hidden = true
  image.src = src
  image.alt = alt
  dialog.setAttribute('aria-label', alt)
  if (!dialog.open) dialog.showModal()
}

export interface Kpi {
  metric: string
  value: number | null
  withheld: boolean
  /** Fractional change against the preceding window of equal length — `0.12`
   *  is +12%. Absent where a comparison is not meaningful: a lifetime total has
   *  no preceding period, and a withheld value has nothing to compare. */
  delta?: number | null
  /** What the delta is measured against, named rather than implied. */
  deltaLabel?: string
  /** Overrides the METRIC_LABELS lookup, for a derived tile like avg view time
   *  that is not a Snap metric name. */
  label?: string
  /** Pre-formatted display value, for a tile whose unit is not a count. */
  display?: string
  /** What the figure is over the window — the foot reads *"30 Day Total"* or
   *  *"30 Day Avg"* (X-17). Defaults to a total. */
  kind?: 'total' | 'avg'
  /** `absolute` renders the change as a signed count (*"+250"*) instead of a
   *  percentage. For a figure that can be negative or near zero, where a
   *  percentage is arithmetic without meaning — −200 → +50 is "−125%" (CO-14).
   *  `delta` then carries the difference, not a fraction. */
  deltaFormat?: 'percent' | 'absolute'
  /** The preceding window's figure, shown on the pill's hover — SS1 keeps it
   *  off the tile, so this does too (7 Oct). `previousDisplay` for a unit. */
  previous?: number | null
  previousDisplay?: string
}

/** The window a KPI row describes — a range of whole days, or Snap's lifetime. */
export type KpiSpan = { days: number } | 'lifetime'

/** Inclusive length of a `YYYY-MM-DD` range in days. */
export function spanDays(from: string, to: string): number {
  const start = Date.parse(`${from}T00:00:00Z`)
  const end = Date.parse(`${to}T00:00:00Z`)
  return Math.round((end - start) / 86_400_000) + 1
}

/**
 * A delta pill.
 *
 * **Saturated green and red are reserved for change and status** — never a
 * brand or series colour (`DESIGN-SYSTEM.md`) — so this is the one place they
 * belong. And direction is never colour alone: the arrow carries it too, and
 * the accessible text spells it out, because a 5% red pill and a 5% green pill
 * are the same shape to a reader who cannot separate the hues.
 */
function Delta({
  value,
  label,
  format = 'percent',
}: {
  value: number
  label: string
  format?: 'percent' | 'absolute'
}) {
  const absolute = format === 'absolute'
  const flat = absolute ? value === 0 : Math.abs(value) < 0.005
  const up = value > 0
  const pct = absolute
    ? abbreviate(Math.abs(value))
    : `${Math.abs(value * 100).toFixed(value >= 1 || value <= -1 ? 0 : 1)}%`
  return (
    <span
      className="px-1.5 py-0.5 text-xs font-semibold"
      style={{
        borderRadius: 'var(--radius-sm)',
        background: flat
          ? 'var(--delta-flat-bg)'
          : up
            ? 'var(--delta-up-bg)'
            : 'var(--delta-down-bg)',
        color: flat ? 'var(--delta-flat-ink)' : up ? 'var(--delta-up-ink)' : 'var(--delta-down-ink)',
        whiteSpace: 'nowrap',
      }}
      title={label}
    >
      <span aria-hidden="true">{flat ? '±' : up ? '▲' : '▼'}</span>
      <span className="sr-only">{flat ? 'no change' : up ? 'up' : 'down'} </span>
      {flat ? (absolute ? '0' : '0%') : pct}
    </span>
  )
}

/**
 * A tile's headline figure: **abbreviated, per HR-3's ladder.**
 *
 * `380.8M`, not `380,845,231`. A tile is read at a glance and digit-group
 * counting defeats that; the table below is where a figure gets *checked*, and
 * that keeps its full number.
 *
 * **The exact value is on the `title`**, so abbreviating costs the reader
 * nothing — it is one hover away rather than gone.
 *
 * Negatives render as red parentheses (HR-3). Real here: a channel can lose
 * subscribers on a day, and `SUBSCRIBERS_GAINED` is negative on 860 rows of a
 * 90-day pull. The parentheses do the work for anyone who cannot see the
 * colour (VQ-14).
 */
function TileValue({ value }: { value: number | null | undefined }) {
  if (value === null || value === undefined) {
    return <span style={{ color: 'var(--text-label)' }}>—</span>
  }
  const { text, negative } = signed(value, abbreviate)
  return (
    <span
      title={formatNumber(value)}
      style={negative ? { color: 'var(--status-critical)' } : undefined}
    >
      {text}
    </span>
  )
}

function KpiFoot({ kpi, span }: { kpi: Kpi; span: KpiSpan }) {
  const compared = !kpi.withheld && kpi.delta !== null && kpi.delta !== undefined
  return (
    <div
      className="kpi-foot mt-0.5 flex justify-between gap-2 text-xs"
      style={{ color: 'var(--text-label)' }}
    >
      {/* A withheld tile has no figure to describe — "Not Derivable · 15 Day
          Total" contradicts itself (seen on Spotlights' Viewers, 7 Oct). The
          space stays so the row's tiles keep one height. */}
      <span>
        {kpi.withheld
          ? '\u00a0'
          : span === 'lifetime'
            ? 'Snap Lifetime'
            : `${span.days} Day ${kpi.kind === 'avg' ? 'Avg' : 'Total'}`}
      </span>
      {compared && span !== 'lifetime' && <span>vs Prior {span.days} Days</span>}
    </div>
  )
}

/**
 * Stat tiles. A withheld metric shows why, rather than vanishing or showing 0.
 *
 * **X-17 — every tile says what it is and what it is against**, SS1's anatomy
 * and the design system's `kpi-foot` (`design-system/index.html`), which the
 * first build never rendered: *"30 Day Total"* under the number, *"vs Prior 30
 * Days"* opposite when — and only when — the tile carries a change. A baseline
 * that lived in one caption under the row (Stories) or nowhere (Channel
 * Overview) is now on the tile it qualifies.
 */
export function KpiRow({
  kpis,
  notes = {},
  span,
}: {
  kpis: Kpi[]
  notes?: Record<string, string>
  /** Omitted, the tiles carry no foot. */
  span?: KpiSpan
}) {
  return (
    <div
      className="mb-4 grid gap-3"
      style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(9rem, 100%), 1fr))' }}
    >
      {kpis.map((kpi) => (
        <div
          key={kpi.metric}
          className="px-3 py-2"
          style={{
            background: 'var(--bg-surface)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-md)',
          }}
        >
          <div className="text-xs" style={{ color: 'var(--text-label)' }}>
            {kpi.label ?? METRIC_LABELS[kpi.metric] ?? kpi.metric}
            {notes[kpi.metric] && <InfoDot note={notes[kpi.metric]!} />}
          </div>
          <div
            // SS1's anatomy (Jesse's reference, 7 Oct): the figure leads, and the
            // change pill sits at the row's right edge rather than against the
            // number. 22px rather than the showcase's 28px `kpi-val`: Stories
            // carries seven tiles a row, and 28px plus a pill overflows a
            // 9rem tile.
            className="flex flex-wrap items-center justify-between gap-x-2 font-bold"
            style={{
              ...numberStyle,
              color: 'var(--text-primary)',
              fontSize: 'var(--text-2xl)',
              lineHeight: 1.25,
            }}
          >
            {kpi.withheld ? (
              <span className="text-sm font-normal" style={{ color: 'var(--text-label)' }}>
                Not Derivable
              </span>
            ) : (
              <>
                {kpi.display ?? <TileValue value={kpi.value} />}
                {kpi.delta !== null && kpi.delta !== undefined && (
                  <Delta
                    value={kpi.delta}
                    label={[
                      kpi.deltaLabel ?? 'Against the Preceding Window of Equal Length',
                      kpi.previousDisplay ??
                        (kpi.previous !== null && kpi.previous !== undefined
                          ? formatNumber(kpi.previous)
                          : null),
                    ]
                      .filter(Boolean)
                      .join(' — Prior: ')}
                    format={kpi.deltaFormat}
                  />
                )}
              </>
            )}
          </div>
          {span && <KpiFoot kpi={kpi} span={span} />}
        </div>
      ))}
    </div>
  )
}

/**
 * Pagination for the dense tables.
 *
 * Not cosmetic. Measured on `/stories`: 349 rows rendered 349 `<img>` elements
 * and fired **382 thumbnail requests** on load — `loading="lazy"` does not save
 * you when every row is in the DOM, and each request is a redirect plus a
 * signed-URL generation on the server. The page never reached network-idle.
 *
 * These tables also grow every day the pipeline runs, so this is not a
 * one-off: a year of stories is ~1,300 rows. Pagination bounds the work to a
 * page, and it is what a reader of a dense BI table expects anyway.
 */
export function Pagination({
  page,
  pageCount,
  rowCount,
  onChange,
}: {
  page: number
  pageCount: number
  rowCount: number
  onChange: (page: number) => void
}) {
  if (pageCount <= 1) return null
  const button = {
    background: 'transparent',
    border: '1px solid var(--border)',
    borderRadius: 'var(--radius-sm)',
    color: 'var(--text-secondary)',
    cursor: 'pointer',
    padding: '0.25rem 0.5rem',
  }
  return (
    <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
      <button
        type="button"
        style={{ ...button, opacity: page === 0 ? 0.4 : 1 }}
        disabled={page === 0}
        onClick={() => onChange(page - 1)}
      >
        ‹ Previous
      </button>
      <span style={{ color: 'var(--text-label)' }}>
        Page {page + 1} of {pageCount} · {formatNumber(rowCount)} rows
      </span>
      <button
        type="button"
        style={{ ...button, opacity: page >= pageCount - 1 ? 0.4 : 1 }}
        disabled={page >= pageCount - 1}
        onClick={() => onChange(page + 1)}
      >
        Next ›
      </button>
    </div>
  )
}

